/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import DoctorFullForm from "components/forms/doctor/DoctorFullForm";
import Button from "components/generals/Button";
import ButtonDialog from "components/generals/ButtonDialog";
import DefaultCell from "components/generals/DefaultCell";
import { Modal } from "components/generals/Modal";
import PermissionWrapper from "components/generals/PermissionWrapper";
import DataTable from "dashboard-template/examples/Tables/DataTable";
import { TableProps } from "interfaces";
import { HeaderEnum, PharmacyInterface, ProviderInterface, UserRolesEnum } from "medicalsystem-shared";
import { FC, useMemo, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { EntityPermissions, FormActionType, PermissionsUser } from "types";
import { getNewNurseDataContent } from "utils/model.utils";
import { useTranslation } from "react-i18next";
import {
  selectIsProvider,
  selectCurrentUser,
  selectUserPermissions,
  selectIsPatient,
} from "store/slices/auth.slice";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";

/**
 * Nurse table component for displaying and managing nurse provider records.
 * 
 * Features:
 * - Displays nurse information in sortable data table
 * - Columns: name, last name, specialty, action buttons
 * - Edit nurse functionality via modal with DoctorFullForm (reused for nurses)
 * - Delete nurse with confirmation dialog and list update
 * - Permission-based action visibility:
 *   - Nurses can only edit their own records
 *   - Patients cannot edit any records
 *   - Admins can edit all records
 * - Role-based filtering for appropriate data access
 * 
 * Security:
 * - Uses HeaderEnum.RESOURCE_ID for secure provider identification
 * - Permission-based rendering via PermissionWrapper
 * - Validates current user ID against provider's user ID for self-edit
 * - Role checks via selectIsProvider, selectIsPatient selectors
 * 
 * Note: Reuses DoctorFullForm component for nurse editing since both
 * doctors and nurses are provider types with similar attributes.
 * 
 * @param {TableProps<ProviderInterface>} props - Table properties
 * @param {ProviderInterface[]} props.modelList - Array of nurse provider records to display
 * @param {Function} props.setModelList - Callback to update nurse list after changes
 * 
 * @returns {JSX.Element} Nurse data table with role-based edit/delete actions
 */
const NurseTable: FC<TableProps<ProviderInterface>> = (props) => {
  const [selectedNurse, setSelectedNurse] =
    useState<ProviderInterface>(getNewNurseDataContent());
  const [activeEditNurseModal, setActiveEditNurseModal] = useState<boolean>(false);

  const { t } = useTranslation();

  const currentUser = useSelector(selectCurrentUser);
  const permissions = useSelector(selectUserPermissions);
  const isProvider = useSelector(selectIsProvider);
  const isPatient = useSelector(selectIsPatient);
  const [_isLoading, _error, sendRequest] = useHttp();

  useEffect(() => {
    if (selectedNurse && selectedNurse.id) {
      const updatedNurse = props.modelList.find(n => n.id === selectedNurse.id);
      if (updatedNurse) {
        setSelectedNurse(updatedNurse);
      }
    }
  }, [props.modelList]);

  const deleteHandler = (providerTemplate: ProviderInterface) => {
    sendRequest<boolean, ProviderInterface>(
      CoreAPI_URLs.PROVIDER_GENERAL_OPERATIONS,
      onProviderDeleteHandler,
      "DELETE",
      JSON.stringify(providerTemplate),
      [{ header: HeaderEnum.RESOURCE_ID, value: providerTemplate.id }],
      { payload: providerTemplate, successMessage: t("nurseTable.nurseDeleted") }
    );
  };
  
  const onProviderDeleteHandler = (result: boolean, provider: ProviderInterface) => {
    const indexList = props.modelList.indexOf(provider);
    props.modelList.splice(indexList, 1);
    props.setModelList([...props.modelList]);
  };

  const getUITable = (list: ProviderInterface[]) => {
    const baseColumns = [
      {
        Header: t("patientTable.name"),
        accessor: "person.name",
        sortIndicatorType: "az",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("patientTable.lastName"),
        accessor: "person.lastname",
        sortIndicatorType: "az",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("patientTable.specialty"),
        accessor: "specialty",
        sortIndicatorType: "az",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("patientTable.action"),
        accessor: "id",
        disableSortBy: true,

        Cell: ({ value, row }: any) => {
          if (isProvider) {
            const isOwnRecord = row.original.person?.user?.id === currentUser.id;
            if (!isOwnRecord) {
              return null;
            }
          }

          return (
            <>
              <PermissionWrapper
                requiredPermissions={[
                  PermissionsUser[EntityPermissions.NURSE].EDIT,
                  PermissionsUser[EntityPermissions.NURSE].VIEW,
                ]}
              >
                {!isPatient && (
                  <Button
                    variant="contained"
                    color="info"
                    sx={{ marginRight: 1 }}
                    onClick={() => {
                      const nurse = list.find((p) => p.id === value);
                      if (nurse) {
                        openModalHandler(nurse);
                      }
                    }}
                  >
                    {t("patientTable.view")}
                  </Button>
                )}
              </PermissionWrapper>

              <PermissionWrapper
                requiredPermissions={[
                  PermissionsUser[EntityPermissions.NURSE].DELETE,
                ]}
              >
                {!isPatient && (
                  <ButtonDialog
                    onAcceptHandle={() => {
                      const nurse = list.find((p) => p.id === value);
                      if (nurse) {
                        if (nurse.person?.user?.id === currentUser.id) {
                          return;
                        }
                        deleteHandler(nurse);
                      }
                    }}
                    buttonText={t("nurseTable.deleteAction")}
                    title={t("nurseTable.deleteConfirmation")}
                    description={t("nurseTable.deleteDescription")}
                    labelledbyID={"NurseDelete"}
                    describedbyID={"NurseDescriptionId"}
                  />
                )}
              </PermissionWrapper>
            </>
          );
        },
      },
    ];

    const hasGenderPermission = permissions.includes(
      PermissionsUser[EntityPermissions.NURSE].VIEW
    );

    if (hasGenderPermission) {
      baseColumns.splice(2, 0, {
        Header: t("patientTable.sex"),
        accessor: "person.sex",
        sortIndicatorType: "az",
        Cell: ({ value }: any) => <DefaultCell value={t(`patientTable.${value}`)} />,
      });
    }

    return {
      columns: baseColumns,
      rows: list,
    };
  };

  const openModalHandler = (nurse: ProviderInterface) => {
    setSelectedNurse(nurse);
    setActiveEditNurseModal(true);
  };

  const dataTableData = useMemo(
    () => getUITable(props.modelList),
    [props.modelList, isProvider, currentUser, permissions]
  );

  return (
    <>
      <DataTable {...props} table={dataTableData} canSearch isLoading={props.isLoading}/>
      <Modal
        open={activeEditNurseModal}
        onClose={() => {
          setActiveEditNurseModal(false);
        }}
      >
        <DoctorFullForm
          isModal={true}
          isDoctor={false}
          modelList={props.modelList}
          setModelList={props.setModelList}
          model={selectedNurse}
          action={FormActionType.MODIFY}
          onCloseAction={() => setActiveEditNurseModal(false)}
          comesFromTable={true}
        />
      </Modal>
    </>
  );
};

export default NurseTable;